const ASSET_BASE =
  process.env.REACT_APP_ASSET_BASE_URL ||
  "https://pub-f0f38b8dd0224e2f89367893cf6bb0d7.r2.dev";

const garmentUrl = (filename) => `${ASSET_BASE}/garments/${filename}`;

const GARMENT_MESH_LIBRARY = {
  top: {
    fitted: garmentUrl("top_fitted.glb"),
    relaxed: garmentUrl("top_relaxed.glb"),
    oversized: garmentUrl("top_oversized.glb"),
  },
  bottom: {
    fitted: garmentUrl("pant_fitted.glb"),
    relaxed: garmentUrl("pant_relaxed.glb"),
    wide: garmentUrl("pant_wide.glb"),
  },
  dress: {
    default: garmentUrl("dress.glb"),
  },
  outer: {
    open: garmentUrl("outer_open.glb"),
    closed: garmentUrl("outer_closed.glb"),
    puffer: garmentUrl("outer_closed.glb"),
  },
};

export function normalizeBrowseCategory(category) {
  const value = String(category || "").toLowerCase().trim();

  if (
    [
      "top","tops","shirt","t-shirt","tee","blouse","knit","sweater","jumper",
      "cardigan","cardi","tank","cami",
    ].includes(value)
  ) return "top";

  if (
    [
      "bottom","bottoms","pants","trousers","jeans","skirt","shorts",
      "legging","leggings","jegging","jeggings",
    ].includes(value)
  ) return "bottom";

  if (["dress","dresses"].includes(value)) return "dress";

  if (
    [
      "outer","outerwear","jacket","coat","puffer","puffer jacket","blazer",
    ].includes(value)
  ) return "outer";

  return value;
}

export function detectTopFitType(text = "") {
  const t = String(text || "").toLowerCase();

  if (
    t.includes("oversized") || t.includes("sweatshirt") || t.includes("hoodie") ||
    t.includes("slouchy") || t.includes("chunky knit") || t.includes("boyfriend") ||
    t.includes("knit") || t.includes("sweater") || t.includes("jumper")
  ) return "oversized";

  if (
    t.includes("fitted") || t.includes("bodycon") || t.includes("tight") ||
    t.includes("contour") || t.includes("second skin") || t.includes("ribbed") ||
    t.includes("cami") || t.includes("tank") || t.includes("crop top")
  ) return "fitted";

  if (
    t.includes("relaxed") || t.includes("loose") ||
    t.includes("cardigan") || t.includes("blouse")
  ) return "relaxed";

  return null;
}

export function detectBottomFitType(text = "") {
  const t = String(text || "").toLowerCase();

  if (
    t.includes("legging") || t.includes("skinny") || t.includes("spray-on")
  ) return "fitted";

  if (
    t.includes("wide leg") || t.includes("palazzo") ||
    t.includes("baggy") || t.includes("flare")
  ) return "wide";

  if (
    t.includes("relaxed") || t.includes("straight") ||
    t.includes("trouser") || t.includes("slim")
  ) return "relaxed";

  return null;
}

export function detectBottomLengthVariant(text = "") {
  const t = String(text || "").toLowerCase();

  if (t.includes("bermuda")) return "bermuda";

  if (
    t.includes("mini short") || t.includes("micro short") ||
    t.includes("hot pant") || t.includes("short short")
  ) return "shorts_mini";

  if (t.includes("short")) return "shorts";

  return null;
}

export function detectDressLengthVariant(product) {
  const text = `${product?.title || ""} ${product?.description || ""}`.toLowerCase();
  if (text.includes("maxi")) return "maxi";
  if (text.includes("midi")) return "midi";
  if (text.includes("mini")) return "mini";
  return null;
}

export function getUnsupportedTryOnReason(product) {
  const category = normalizeBrowseCategory(product?.category);
  const text = `${product?.title || ""} ${product?.description || ""}`.toLowerCase();

  if (text.includes("turtleneck")) return "unsupported_high_neck";
  if (category === "outer" && text.includes("long coat")) return "unsupported_long_outer";
  if (category === "bottom" && text.includes("skirt")) return "unsupported_skirt";
  if (category === "top" && text.includes("singlet")) return "unsupported_singlet";

  return null;
}

export function resolveMeshUrl(product) {
  const category = normalizeBrowseCategory(product?.category);
  const text = `${product?.title || ""} ${product?.description || ""}`.toLowerCase();

  if (product?.meshUrl) return product.meshUrl;
  if (product?.mesh_url) return product.mesh_url;

  if (getUnsupportedTryOnReason(product)) return null;

  const lib = GARMENT_MESH_LIBRARY[category];
  if (!lib) return null;

  // ✅ FIT RESOLUTION (override → inferred → detection)
  const fit =
    product?.try_on?.override_fit ??
    product?.try_on?.inferred_fit ??
    (category === "top"
      ? detectTopFitType(text)
      : category === "bottom"
      ? detectBottomFitType(text)
      : null);

  if (category === "top") {
    return lib[fit] || lib.relaxed;
  }

  if (category === "bottom") {
    if (fit === "fitted") return lib.fitted;
    if (fit === "wide") return lib.wide;
    return lib.relaxed;
  }

  if (category === "outer") {
    return lib.closed;
  }

  if (category === "dress") {
    return lib.default;
  }

  return null;
}

export function normalizeBackendProduct(product) {
  const gallery = Array.isArray(product?.images)
    ? product.images.map((img) => img?.url).filter(Boolean)
    : [];

  const generated = product?.try_on?.asset_generation || {};

  const generatedFront =
    generated?.front?.asset_url ||
    generated?.front?.assetUrl ||
    null;

  const generatedBack =
    generated?.back?.asset_url ||
    generated?.back?.assetUrl ||
    null;

  const selected = product?.try_on?.selected_images || {};

  const selectedFront = selected.front || null;
  const selectedBack = selected.back || null;

  // ✅ PRIORITY: generated → fallback to selected
  const frontImage = generatedFront || selectedFront || null;
  const backImage = generatedBack || selectedBack || null;

  const normalizedCategory = normalizeBrowseCategory(product?.category);
  const meshUrl = resolveMeshUrl(product);

  const text = `${product?.title || ""} ${product?.description || ""}`.toLowerCase();

  let variant = null;

  // ✅ VARIANT RESOLUTION (override → inferred → detection)
  if (normalizedCategory === "bottom") {
    variant =
      product?.try_on?.override_variant ??
      product?.try_on?.inferred_variant ??
      detectBottomLengthVariant(text);
  }

  if (normalizedCategory === "dress") {
    variant =
      product?.try_on?.override_variant ??
      product?.try_on?.inferred_variant ??
      detectDressLengthVariant(product);
  }

  return {
    ...product,
    name: product?.title,
    brand:
      product?.brand ||
      product?.merchant_name ||
      product?.store_name ||
      "Unknown Store",
image: frontImage || product?.image_url || gallery[0] || null,
imageBack: backImage || null,
    productUrl: product?.product_url || null,
    variant,
    modelCategory: null,
    category: normalizedCategory,
    meshUrl,
    images: gallery,
    unsupportedTryOnReason: getUnsupportedTryOnReason(product),
  };
}